import type { CrmEntityType } from '@plato/data/model/crm-config';
import { presetFields } from '@plato/data/model/crm-config';
import type { TFunction } from 'i18next';

export function getCrmFieldLabel(
    t: TFunction<'customer-detail'>,
    entity: CrmEntityType,
    path: string,
): string | undefined {
    switch (entity) {
        case 'address':
            return getAddressFieldLabel(t, path);
        case 'contact':
            return getContactFieldLabel(t, path);
        case 'customer':
            return getCustomerFieldLabel(t, path);
    }
}

export function getCrmFieldPlaceholder(
    t: TFunction<'customer-detail'>,
    entity: CrmEntityType,
    path: string,
): string | undefined {
    switch (entity) {
        case 'address':
            return undefined;
        case 'contact':
            return getContactFieldPlaceholder(t, path);
        case 'customer':
            return undefined;
    }
}

function getAddressFieldLabel(t: TFunction<'customer-detail'>, path: string): string | undefined {
    switch (path) {
        case '/canReceiveGoodsAtNight':
            return t('field_label__address__canReceiveGoodsAtNight', { ns: 'customer-detail' });
        case '/canReceiveParcels':
            return t('field_label__address__canReceiveParcels', { ns: 'customer-detail' });
        case '/comment':
            return t('field_label__address__comment', { ns: 'customer-detail' });
        case '/isPreferredShippingAddress':
            return t('field_label__address__isPreferredShippingAddress', { ns: 'customer-detail' });
        case presetFields.address.formatted:
            return t('field_label__address__preset__address_formatted', { ns: 'customer-detail' });
        default:
            return undefined;
    }
}

function getContactFieldLabel(t: TFunction<'customer-detail'>, path: string): string | undefined {
    switch (path) {
        case '/birthday':
            return t('field_label__contact__birthday', { ns: 'customer-detail' });
        case '/comment':
            return t('field_label__contact__comment', { ns: 'customer-detail' });
        case '/department':
            return t('field_label__contact__department', { ns: 'customer-detail' });
        case '/email':
            return t('field_label__contact__email', { ns: 'customer-detail' });
        case '/fax':
            return t('field_label__contact__fax', { ns: 'customer-detail' });
        case '/firstName':
            return t('field_label__contact__firstName', { ns: 'customer-detail' });
        case '/lastName':
            return t('field_label__contact__lastName', { ns: 'customer-detail' });
        case '/mobile':
            return t('field_label__contact__mobile', { ns: 'customer-detail' });
        case '/name':
            return t('field_label__contact__name', { ns: 'customer-detail' });
        case '/phone':
            return t('field_label__contact__phone', { ns: 'customer-detail' });
        case '/role':
            return t('field_label__contact__role', { ns: 'customer-detail' });
        case '/salutation':
            return t('field_label__contact__salutation', { ns: 'customer-detail' });
        default:
            return undefined;
    }
}

function getCustomerFieldLabel(t: TFunction<'customer-detail'>, path: string): string | undefined {
    switch (path) {
        case '/annualSalesPotential':
            return t('field_label__customer__annualSalesPotential', { ns: 'customer-detail' });
        case '/associationMemberships':
            return t('field_label__customer__associationMemberships', { ns: 'customer-detail' });
        case '/branch':
            return t('field_label__customer__branch', { ns: 'customer-detail' });
        case '/customerType':
            return t('field_label__customer__customerType', { ns: 'customer-detail' });
        case '/dunningLevel':
            return t('field_label__customer__dunningLevel', { ns: 'customer-detail' });
        case '/emailAddress':
            return t('field_label__customer__emailAddress', { ns: 'customer-detail' });
        case '/employeeCount':
            return t('field_label__customer__employeeCount', { ns: 'customer-detail' });
        case '/facebookUrl':
            return t('field_label__customer__facebookUrl', { ns: 'customer-detail' });
        case '/faxNumber':
            return t('field_label__customer__faxNumber', { ns: 'customer-detail' });
        case '/firstOrderDate':
            return t('field_label__customer__firstOrderDate', { ns: 'customer-detail' });
        case '/industrySector':
            return t('field_label__customer__industrySector', { ns: 'customer-detail' });
        case '/lastOrderDate':
            return t('field_label__customer__lastOrderDate', { ns: 'customer-detail' });
        case '/linkedInUrl':
            return t('field_label__customer__linkedInUrl', { ns: 'customer-detail' });
        case '/openOrderCount':
            return t('field_label__customer__openOrderCount', { ns: 'customer-detail' });
        case '/openQuoteCount':
            return t('field_label__customer__openQuoteCount', { ns: 'customer-detail' });
        case '/orderBlock':
            return t('field_label__customer__orderBlock', { ns: 'customer-detail' });
        case '/overdueInvoiceCount':
            return t('field_label__customer__overdueInvoiceCount', { ns: 'customer-detail' });
        case '/ownExhibition':
            return t('field_label__customer__ownExhibition', { ns: 'customer-detail' });
        case '/paymentTerms':
            return t('field_label__customer__paymentTerms', { ns: 'customer-detail' });
        case '/phoneNumber':
            return t('field_label__customer__phoneNumber', { ns: 'customer-detail' });
        case '/potentialReachedTtm':
            return t('field_label__customer__potentialReachedTtm', { ns: 'customer-detail' });
        case '/potentialReachedYtd':
            return t('field_label__customer__potentialReachedYtd', { ns: 'customer-detail' });
        case '/priceGroup':
            return t('field_label__customer__priceGroup', { ns: 'customer-detail' });
        case '/quoteWinRateTtm':
            return t('field_label__customer__quoteWinRateTtm', { ns: 'customer-detail' });
        case '/region':
            return t('field_label__customer__region', { ns: 'customer-detail' });
        case '/revenueTtm':
            return t('field_label__customer__revenueTtm', { ns: 'customer-detail' });
        case '/revenueYtd':
            return t('field_label__customer__revenueYtd', { ns: 'customer-detail' });
        case '/taxNumber':
            return t('field_label__customer__taxNumber', { ns: 'customer-detail' });
        case '/totalOrderCount':
            return t('field_label__customer__totalOrderCount', { ns: 'customer-detail' });
        case '/unbilledLineItemTotal':
            return t('field_label__customer__unbilledLineItemTotal', { ns: 'customer-detail' });
        case '/websiteUrl':
            return t('field_label__customer__websiteUrl', { ns: 'customer-detail' });
        case presetFields.customer.address:
            return t('field_label__customer__preset__customer_address', { ns: 'customer-detail' });
        case presetFields.customer.creditLimit:
            return t('field_label__customer__preset__customer_creditLimit', { ns: 'customer-detail' });
        case presetFields.customer.externalSalesReps:
            return t('field_label__customer__preset__customer_externalSalesReps', { ns: 'customer-detail' });
        case presetFields.customer.gridCreditLimit:
            return t('field_label__customer__preset__customer_gridCreditLimit', { ns: 'customer-detail' });
        case presetFields.customer.internalSalesReps:
            return t('field_label__customer__preset__customer_internalSalesReps', { ns: 'customer-detail' });
        default:
            return undefined;
    }
}

function getContactFieldPlaceholder(t: TFunction<'customer-detail'>, path: string): string | undefined {
    switch (path) {
        case '/birthday':
            return t('field_placeholder__contact__birthday', { ns: 'customer-detail' });
        case '/comment':
            return t('field_placeholder__contact__comment', { ns: 'customer-detail' });
        case '/department':
            return t('field_placeholder__contact__department', { ns: 'customer-detail' });
        case '/email':
            return t('field_placeholder__contact__email', { ns: 'customer-detail' });
        case '/mobile':
            return t('field_placeholder__contact__mobile', { ns: 'customer-detail' });
        case '/name':
            return t('field_placeholder__contact__name', { ns: 'customer-detail' });
        case '/phone':
            return t('field_placeholder__contact__phone', { ns: 'customer-detail' });
        case '/role':
            return t('field_placeholder__contact__role', { ns: 'customer-detail' });
        case '/salutation':
            return t('field_placeholder__contact__salutation', { ns: 'customer-detail' });
        case '/title':
            return t('field_placeholder__contact__title', { ns: 'customer-detail' });
        default:
            return undefined;
    }
}
